import React from "react";
import { Row, Col, OverlayTrigger, Popover } from "react-bootstrap";
import { bool, func, number, object } from "prop-types";
import Toggle from "react-toggle";
import infoIcon from "resources/images/info.svg";
import "react-toggle/style.css";
import "!style-loader!css-loader!./styles.css";
import { PROCTORING_CONFIGURATION_DEFAULT_SETTINGS } from "utils/proctoringHelpers";
import { PROCTORING_SUBCONTROLS_LIBRARY } from "./helperConstants";

/**
 * ProctoringSubControlSection
 *
 * The component shows toggle switches for each setting that can be turned on/off, along with
 * descriptive text about each feature. It respects the parent proctoring toggle state and displays
 * a saved message when changes are made if savedMessages is provided.
 *
 * @param {function} handleFeatureToggle - Function to handle toggle state changes
 * @param {boolean} isProctoringEnabled - Whether the main proctoring feature is enabled
 * @param {number} isProctoringOptedIn - Whether the user has opted in to proctoring, 0 = disabled, 1 = enabled with AI features, 2 = enabled without AI features
 * @param {object} proctoringConfiguration - Object containing proctoring settings
 * @param {object} savedMessages - Object containing save status for each toggle if provided
 * @returns {React.Component} - The rendered component
 */
const ProctoringSubControlSection = ({
  handleFeatureToggle,
  isProctoringEnabled = false,
  isProctoringOptedIn = 0,
  proctoringConfiguration = PROCTORING_CONFIGURATION_DEFAULT_SETTINGS.disabled,
  savedMessages = null,
}) => {
  const subControlToggleSections = Object.keys(PROCTORING_SUBCONTROLS_LIBRARY)
    .sort(
      (a, b) =>
        PROCTORING_SUBCONTROLS_LIBRARY[a].sortOrder -
        PROCTORING_SUBCONTROLS_LIBRARY[b].sortOrder
    )
    .map((key, index) => {
      const { label, description } = PROCTORING_SUBCONTROLS_LIBRARY[key];

      const isToggleDisabled =
        !isProctoringEnabled ||
        (isProctoringOptedIn > 0
          ? !PROCTORING_CONFIGURATION_DEFAULT_SETTINGS.enabled[
              isProctoringOptedIn
            ][key]
          : false);

      return (
        <div key={key}>
          <Row>
            <Col lg={10} md={10} sm={10} className="d-flex align-items-center">
              <div>
                {label}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Popover
                      id={`${key}-tooltip`}
                      className="proctoring-subcontrol-tooltip"
                    >
                      {description}
                    </Popover>
                  }
                >
                  <img
                    height="16px"
                    width="16px"
                    src={infoIcon}
                    alt="Info"
                    className="ml-2 mb-1"
                  />
                </OverlayTrigger>
              </div>
            </Col>
            <Col lg={2} md={2} sm={2} className="text-right pr-0">
              <div style={{ margin: "auto" }}>
                {savedMessages && savedMessages[key] ? (
                  <span
                    className="mr-2 text-muted"
                    style={{
                      position: "relative",
                      top: "-7px",
                      fontSize: "12px",
                    }}
                  >
                    Setting Saved
                  </span>
                ) : null}
                <Toggle
                  id={key}
                  icons={false}
                  checked={isProctoringEnabled && proctoringConfiguration[key]}
                  onChange={handleFeatureToggle(key)}
                  disabled={isToggleDisabled}
                  className="small-toggle"
                />
              </div>
            </Col>
          </Row>
          {index < Object.keys(PROCTORING_SUBCONTROLS_LIBRARY).length - 1 && (
            <hr />
          )}
        </div>
      );
    });

  return (
    <Col className="p-3 proctoring-subcontrol-section">
      {subControlToggleSections}
    </Col>
  );
};

ProctoringSubControlSection.propTypes = {
  handleFeatureToggle: func.isRequired,
  isProctoringEnabled: bool,
  isProctoringOptedIn: number,
  proctoringConfiguration: object,
  savedMessages: object,
};

export default ProctoringSubControlSection;
